import { arrayMove } from '@dnd-kit/sortable';
import { getAnnotationKey, getTopY } from './annotationUtils';

const useDragDropHandlers = ({
                                 annotationOrder,
                                 setAnnotationOrder,
                                 groupAnnotationOrder,
                                 setGroupAnnotationOrder,
                                 selectedAnnotations,
                                 setAnnotations,
                                 setGroupMeta // Add setGroupMeta to dependencies
                             }) => {
    const END_DROP_ID = '__end__';

    const handleDragEnd = (event) => {
        const { active, over } = event;
        if (!over || active.id === over.id) return;

        const activeId = active.id;
        const overId = over.id;

        const activeGroup = Object.entries(groupAnnotationOrder).find(([_, items]) => items.includes(activeId))?.[0];
        const overGroup = Object.entries(groupAnnotationOrder).find(([_, items]) => items.includes(overId))?.[0];

        const isActiveGrouped = Boolean(activeGroup);
        const isOverGrouped = Boolean(overGroup);

        if (isActiveGrouped && (overId === END_DROP_ID || annotationOrder.includes(overId))) {
            const cleanGroupAnnotationOrder = { ...groupAnnotationOrder };
            for (const groupId in cleanGroupAnnotationOrder) {
                cleanGroupAnnotationOrder[groupId] = cleanGroupAnnotationOrder[groupId].filter(id => id !== activeId);
            }

            const cleanAnnotationOrder = annotationOrder.filter(id => id !== activeId);

            const overIndex = annotationOrder.indexOf(overId);
            const newIndex = overId === END_DROP_ID ? annotationOrder.length : overIndex;

            const newAnnotationOrder = [
                ...cleanAnnotationOrder.slice(0, newIndex),
                activeId,
                ...cleanAnnotationOrder.slice(newIndex)
            ];

            setGroupAnnotationOrder(cleanGroupAnnotationOrder);
            setAnnotationOrder(newAnnotationOrder);
            setAnnotations(prev =>
                prev.map(a => getAnnotationKey(a) === activeId // Use getAnnotationKey for comparison
                    ? { ...a, groupId: null, groupName: null }
                    : a
                )
            );
            return;
        }

        // Moving within the same group
        if (isActiveGrouped && activeGroup === overGroup) {
            const updated = arrayMove(
                groupAnnotationOrder[activeGroup],
                groupAnnotationOrder[activeGroup].indexOf(activeId),
                groupAnnotationOrder[activeGroup].indexOf(overId)
            );
            setGroupAnnotationOrder(prev => ({
                ...prev,
                [activeGroup]: updated
            }));
            return;
        }

        const isOverTopLevel = annotationOrder.includes(overId);

        // Moving from group to top-level
        if (isActiveGrouped && isOverTopLevel) {
            setGroupAnnotationOrder(prev => {
                const cleaned = {};
                for (const [gid, ids] of Object.entries(prev)) {
                    cleaned[gid] = ids.filter(id => id !== activeId);
                }
                return cleaned;
            });
            setAnnotationOrder(prev => {
                // Prevent duplicate insertions
                if (prev.includes(activeId)) return prev;

                const clean = prev.filter(id => id !== activeId);
                const overIndex = clean.indexOf(overId);
                if (overIndex === -1) return [...clean, activeId]; // fallback to end

                return [
                    ...clean.slice(0, overIndex),
                    activeId,
                    ...clean.slice(overIndex),
                ];
            });

            setAnnotations(prev =>
                prev.map(a => (getAnnotationKey(a) === activeId ? { ...a, groupId: null, groupName: null } : a)) // Use getAnnotationKey
            );
            return;
        }

        // Moving from top-level into a group
        if (!isActiveGrouped && isOverGrouped) {
            const targetGroupId = overGroup;

            // 1. Add to group
            setGroupAnnotationOrder(prev => ({
                ...prev,
                [targetGroupId]: [...(prev[targetGroupId] || []), activeId]
            }));

            // 2. Remove from top-level
            setAnnotationOrder(prev => {
                const cleaned = prev.filter(id => id !== activeId);

                // Ensure the group exists in order if it's the first annotation being grouped
                const groupKey = `group_${targetGroupId}`;
                if (!cleaned.includes(groupKey)) {
                    // Get top-most annotation to sort group in
                    const allAnns = selectedAnnotations;
                    const groupAnns = allAnns.filter(a => a.groupId === targetGroupId || getAnnotationKey(a) === activeId); // Use getAnnotationKey
                    const getAnnoTopY = a => a.bbox?.[1] ?? a.field?.bbox?.[1] ?? a.value?.bbox?.[1] ?? Infinity;
                    const top = groupAnns.reduce((minA, a) => getAnnoTopY(a) < getAnnoTopY(minA) ? a : minA, groupAnns[0]);
                    const groupEntry = { id: groupKey, page: top.page, topY: getAnnoTopY(top) };

                    const sorted = [
                        ...cleaned.map(id => {
                            const a = allAnns.find(a => getAnnotationKey(a) === id); // Use getAnnotationKey
                            const page = a?.page ?? 0;
                            const y = getAnnoTopY(a);
                            return { id, page, topY: y };
                        }),
                        groupEntry
                    ].sort((a, b) => a.page !== b.page ? a.page - b.page : a.topY - b.topY);

                    return sorted.map(e => e.id);
                }

                return cleaned;
            });

            // 3. Set groupId on annotation
            setAnnotations(prev =>
                prev.map(a => (getAnnotationKey(a) === activeId ? { ...a, groupId: targetGroupId } : a)) // Use getAnnotationKey
            );

            return;
        }

        // Moving between groups
        if (isActiveGrouped && isOverGrouped && activeGroup !== overGroup) {
            setGroupAnnotationOrder(prev => {
                const newSource = prev[activeGroup].filter(id => id !== activeId);
                const newTarget = [...(prev[overGroup] || []), activeId];

                return {
                    ...prev,
                    [activeGroup]: newSource,
                    [overGroup]: newTarget
                };
            });

            setAnnotations(prev =>
                prev.map(a => (getAnnotationKey(a) === activeId ? { ...a, groupId: overGroup } : a)) // Use getAnnotationKey
            );
            return;
        }

        // Reorder top-level items
        if (!isActiveGrouped && !isOverGrouped) {
            const oldIndex = annotationOrder.indexOf(activeId);
            const newIndex = annotationOrder.indexOf(overId);
            setAnnotationOrder(arrayMove(annotationOrder, oldIndex, newIndex));
        }
    };

    return {
        handleDragEnd,
        END_DROP_ID,
    };
};

export default useDragDropHandlers;